<template>
  <div>
    <h3>What is center of mass?</h3>
    <p>
      Center of mass of a body or a system of particles is a unique point where the entire mass of the system can
      be assumed to be concentrated. Its motion is same as if whole mass of the system was concentrated at this
      point and all the external forces were applied at this point. The concept of center
      of mass helps in solving many complex problems quite simply. Suppose if several forces are acting on a body
      of any odd shape, we need not bother about the forces acting on the individual particles, but we can apply the
      net force on the center of mass and thus analyze the body's motion.
    </p>
    <h3>Center of Mass of System of Particles</h3>
    <p>
      The position of center of mass of a system of particles depends upon the distribution of mass in it. It is
      not necessary that the center of mass lies within the material of the body.
    </p>
    <p>
      For a system of \(n\) particles with masses \(m_1,m_2, ..., m_n\) and position vectors as \(\vec{r_1},\vec{r_2}, ...,\vec{r_n}\), the
      position
      vector \(\vec{r}_{com}\) of the center of mass of the system is given by
    </p>
    $$\vec{r}_{com} =\frac{m_1\vec{r_1}+m_2\vec{r_2}+...+m_n\vec{r_n}}{m_1+m_2+...+m_n}
    =\frac{\sum_{i=1}^{n} \vec{r_i}m_i}{\sum_{i=1}^{n} m_i} \;\;\;\;\;\; -(i)

    $$
    <p>Therefore the \(x\) , \(y\) and \(z\) co-ordinates of center of mass is given by </p>
    $$ x_{com} =\frac{\sum_{i=1}^{n}x_im_i}{M} \;\;\;\; y_{com} =\frac{\sum_{i=1}^{n}y_im_i}{M} \;\;\;\; z_{com}
    =\frac{\sum_{i=1}^{n}z_im_i}{M} $$
    <p>where \(M\) is the total mass of the body.</p>
    <h3>Center of Mass of a Continuous Body</h3>
    <p>
      In case of extended objects where distribution of mass is continuous, above formula cannot work since
      number of particles are very large (tending to infinity) in continuous bodies . Therefore, the summation of
      particles in above formula is replaced by integration of small particles of mass dm. The co-ordinates of
      center of mass
      is now given by
    </p>
    $$ x_{com}=\frac{\int x\,dm}{M} \;\;\;\; y_{com} =\frac{\int y\,dm}{M} \;\;\;\; z_{com} =\frac{\int z\,dm}{M} $$

    <p>where \(M\) is the total mass of the body.</p>
    <p>Center of mass of some common regular objects are given below.</p>
    <table border="4" bordercolor="black" align="center">
      <tr>
        <th>Object</th>
        <th>Position of center of mass</th>
      </tr>
      <tr>
        <td>Uniform Rod</td>
        <td>Mid-point of the rod</td>
      </tr>
      <tr>
        <td>Circular plate, circular ring and a solid sphere </td>
        <td>At their center</td>
      </tr>
      <tr>
        <td> Semi-circular ring </td>
        <td>At a height equal to \(\frac{2R}{\pi}\) from its center on the axis of symmetry</td>
      </tr>
      <tr>
        <td>Semi-circular disc </td>
        <td>
          At a height equal to \(\frac{4R}{3\pi}\) from its center on the axis of symmetry
        </td>
      </tr>
      <tr>
        <td> Hemispherical shell </td>
        <td>
          At a height equal to \(\frac{R}{2}\) from its center on the axis of symmetry
        </td>
      </tr>
      <tr>
        <td>Solid hemisphere </td>
        <td>At a height equal to \( \frac{3R}{8}\) from its center on the axis of symmetry</td>
      </tr>
      <tr>
        <td>Solid cone </td>
        <td>At a height \(\frac{h}{4} \) from its base on the axis</td>
      </tr>
      <tr>
        <td>Hollow cone </td>
        <td>At a height \(\frac{h}{3} \) from its base on the axis</td>
      </tr>
      <tr>
        <td>Triangular Lamina</td>
        <td>At the point of intersection of medians of the triangular lamina</td>
      </tr>
    </table>
    <h3>Motion of center of mass</h3>
    <p>
      Let us now analyze the motion of center of mass of a system where mass of the individual particles are \(m_1, m_2...,m_n\). We
      may rewrite Eq. (i) as

      $$M\vec{r}_{com}=m_1\vec{r_1}+m_2\vec{r_2}+...+m_n\vec{r_n}$$
      On differentiating the above equation with respect to time we get,

      $$M\frac{d\vec{r}_{com}}{dt}=m_1\frac{d\vec{r_1}}{dt}+ m_2\frac{d \vec{r_2}}{dt}+...+m_n\frac{d \vec{r_n}}{dt}$$
      $$ \text{or}\;\;\;\;\;\;\;\;\;
      M \vec{v}_{com}=m_1\vec{v_1}+m_2\vec{v_2}+...+m_n\vec{v_n} \;\;\;\;\;\; -(ii)$$
      $$\\\text{or} \;\;\;\;\;\;\;\;\; \vec{v}_{com}=\frac{\sum_{i=1}^{n}m_iv_i}{M} \;\;\;\;\;\; -(iii)$$
    </p>
    <p>
      Thus, Eq. (iii) gives us the velocity of the center of mass of the system where \(\vec{v_1},\vec{v_2},...,\vec{v_r}\) are the
      velocities of the
      individual particles. Here we have assumed that the total mass of the system remains constant i.e., no mass
      enters or leaves the system.
    </p>

    <p>
      We know that the product of mass and velocity is equal to the momentum of a particle. Therefore, on rewriting Eq. (ii) we can get the
      momentum \(\vec{p}_{com}\) of center of mass of a system of particles. It is given by
      $$\vec{p}_{com}=\vec{p_1}+\vec{p_2}+...+\vec{p_n}$$
      where \(\vec{p_1},\vec{p_2},...,\vec{p_n}\)
      are the
      momentum of individual particles of the system.
    </p>

    <p>
      Now on differentiating Eq. (ii) with respect to time we get,
      $$ M \vec{a}_{com}=m_1\vec{a_1}+m_2\vec{a_2}+...+m_n\vec{a_n} \;\;\;\;\;\; -(iv)$$

      The above equation gives the acceleration \(\vec{a}_{com}\) of the center of mass of a system of \(n\) particles having
      acceleration
      \(\vec{a_1},\vec{a_2},...,\vec{a_n}\).
      We know that force applied on a body is equal to the product of its mass and acceleration i.e, \(F=ma\).
      Therefore, Eq. (iv) can be rewritten as
      $$M\vec{a}_{com}= \vec{F_1}+\vec{F_2}+...+\vec{F_n}$$
      Here \(\vec{F}_1\) is the force acting on the first particle, \(\vec{F}_2\) is the force acting on the second particle and
      so on.
      Since the internal forces are always equal and opposite
      in direction and their sum is zero, only external forces contribute in Eq. (iv). Thus, for a system of particles
      with total mass \(M\) and \(\vec{F}_{ext}\) as the
      vector sum of all the external forces acting on the system, acceleration \(\vec{a}_{com}\) of the center of mass
      is given by
      $$ \vec{F}_{ext} = M\vec{a}_{com} $$
    </p>
    <h3>MagicGraph | Center of Mass of a System of Particles </h3>
    <h5> To Get Started</h5>
    <p>
      Your goal is to balance the structure on the fulcrum.
      This will happen if the line of gravitational force acting on the structure passes through the fulcrum.
      The gravity acts downward and at the center of mass of the structure.
      So, the above task can be accomplished by aligning the center of mass and the fulcrum along a vertical line.
      You can shift the center of mass of the system by tapping on the blank squares. Each colored square has a mass of 1 kilogram.
    </p>
    <v-responsive>
      <v-layout align-center justify-center>
        <div id="jxgbox1" class="edliy-box-about" />
      </v-layout>
    </v-responsive>
  </div>
</template>
<script>
import Boxes from './Boxes.js'
export default {
  name: 'CM',
  created: function () {
    this.$store.commit('navigation/resetState');
    let title = 'Center of Mass';
    this.$store.commit('navigation/changeTitle', title);
    this.$store.commit('navigation/changeMenu', title);
    let newTopics = [
      {title: 'What is Center of Mass?', img:'/assets/number-1.svg', action: () => this.goto('ia')},
      {title: 'System of Discrete Particles', img:'/assets/number-2.svg', action: () => this.goto('ja')},
      {title: 'Continuous Bodies', img:'/assets/number-3.svg', action: () => this.goto('ka')},
      {title: 'MagicGraph' ,img:'/assets/touch.svg', action: () => this.goto('graphical')},
    ];
    this.$store.commit('navigation/replaceTopics', newTopics);
    let newshowhome = false;
    this.$store.commit('navigation/toggleshowhome', newshowhome);
    let newPhys =true;
    this.$store.commit('navigation/replacePhys', newPhys);
    let newLeftArrow =true;
    this.$store.commit('navigation/replaceLeftArrow', newLeftArrow);
    let newModule =true;
    this.$store.commit('navigation/replaceModule', newModule);
  },
  mounted () {
    MathJax.Hub.Queue(["Typeset", MathJax.Hub]);
    Boxes.box1();
  },
  metaInfo: {
          title: 'Center of Mass',
          //titleTemplate: '%s - Learn interactively',
          meta: [
                  {vmid:'description', name: 'description', content: 'Learn interactively how to find center of mass of an object'}
                ]
   },
}
</script>
<style lang="scss">
@import 'src/styles/edliy-box.scss';
@import 'src/styles/subtopic-menu.scss';
@import 'src/styles/edliy-box-about.scss';
@import 'src/styles/screen-sizes.scss';
</style>
